import { zodSchemes } from '@approvalmax/utils';
import { constants, selectors } from 'modules/common';
import { isSortCodeValid } from 'shared/helpers';
import { z } from 'zod';

import { excludeEmptyPersons, isValidXeroContactName } from '../../../selectors/xero';
import { messages as contactInformationPartMessages } from './components/ContactInformationPart/ContactInformationPart.messages';
import { messages } from './XeroContactRequestForm.messages';

const personSchema = z.object({
    firstName: z
        .string()
        .trim()
        .min(1, messages.requiredField({ field: contactInformationPartMessages.firstName }))
        .max(constants.xeroConstants.FIRST_NAME_MAX_LENGTH),
    lastName: z.string().trim().max(constants.xeroConstants.LAST_NAME_MAX_LENGTH).optional(),
    emailAddress: zodSchemes.optional.textField.email(constants.xeroConstants.EMAIL_ADDRESS_MAX_LENGTH),
});

export const phoneSchema = z.object({
    countryCode: z
        .string()
        .max(constants.xeroConstants.PHONE_COUNTRY_CODE_MAX_LENGTH)
        .refine(selectors.ui.isValidPhone)
        .optional(),
    areaCode: z
        .string()
        .max(constants.xeroConstants.PHONE_AREA_CODE_MAX_LENGTH)
        .refine(selectors.ui.isValidPhone)
        .optional(),
    number: z
        .string()
        .max(constants.xeroConstants.PHONE_NUMBER_MAX_LENGTH)
        .refine(selectors.ui.isValidPhone)
        .optional(),
});

const settingsSchema = z.object({
    defaultAccount: zodSchemes.optional.select.string,
    trackingCategory1: zodSchemes.optional.select.string,
    trackingCategory2: zodSchemes.optional.select.string,
});

export const addressSchema = z.object({
    attentionTo: zodSchemes.optional.textField.string,
    address: zodSchemes.optional.textField.string,
    city: zodSchemes.optional.textField.string,
    region: zodSchemes.optional.textField.string,
    postalCode: zodSchemes.optional.textField.string,
    country: zodSchemes.optional.textField.string,
});

export const schema = z
    .object({
        name: zodSchemes.required.textField
            .string(messages.requiredField({ field: contactInformationPartMessages.contactName }))
            .max(constants.xeroConstants.CONTACT_NAME_MAX_LENGTH)
            .refine(isValidXeroContactName, messages.invalidContactName({})),
        accountNumber: z.string().trim().max(constants.xeroConstants.ACCOUNT_NUMBER_MAX_LENGTH).optional(),
        primaryPersonFirstName: z.string().trim().max(constants.xeroConstants.FIRST_NAME_MAX_LENGTH).optional(),
        primaryPersonLastName: z.string().trim().max(constants.xeroConstants.LAST_NAME_MAX_LENGTH).optional(),
        primaryPersonEmail: zodSchemes.optional.textField.email(constants.xeroConstants.EMAIL_ADDRESS_MAX_LENGTH),
        persons: personSchema.array().optional(),
        phone: phoneSchema.optional(),
        fax: phoneSchema.optional(),
        mobile: phoneSchema.optional(),
        directDial: phoneSchema.optional(),
        skypeName: z.string().optional(),
        billingAddress: addressSchema.optional(),
        deliveryAddress: addressSchema
            .extend({
                sameAsBillingAddress: z.boolean().optional(),
            })
            .optional(),
        salesSettings: settingsSchema,
        purchaseSettings: settingsSchema,
        taxNumber: z.string().trim().max(constants.xeroConstants.TAX_NUMBER_MAX_LENGTH).optional(),
        companyNumber: z.string().trim().max(constants.xeroConstants.COMPANY_NUMBER_MAX_LENGTH).optional(),
        salesTax: zodSchemes.optional.select.string,
        purchaseTax: zodSchemes.optional.select.string,
        defaultCurrency: zodSchemes.optional.select.string,
        bankAccount: z
            .object({
                sortCode: z.string().max(constants.xeroConstants.sortCodeMaxLength).optional(),
                accountNumber: z.string().max(constants.xeroConstants.BANK_ACCOUNT_DETAILS_MAX_LENGTH).optional(),
            })
            .optional()
            .superRefine((value, context) => {
                if (!value?.sortCode) {
                    return z.NEVER;
                }

                if (!isSortCodeValid(value.sortCode)) {
                    context.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: messages.invalidSortCode,
                        path: ['sortCode'],
                    });
                }

                if (!/^\d{8}$/.test(value.accountNumber ?? '')) {
                    context.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: messages.invalidBankAccountDetails,
                        path: ['accountNumber'],
                    });
                }
            }),
    })
    .refine(
        (data) => {
            if ((data.persons ?? []).filter(excludeEmptyPersons).length > 0 && !data.primaryPersonEmail) {
                return false;
            }

            return true;
        },
        {
            message: messages.emailIsEmpty,
            path: ['primaryPersonEmail'],
        }
    );
